import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  CopyButton,
  FileButton,
  Group,
  List,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Textarea,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IMPORT_FORMAT, IMPORT_PARTS, IMPORT_VERSION, type ImportDocument, type ImportIssue, type ImportPart, checkImportDocument, issuesAsText, shortenTooLong } from '@pst/shared';
import { ApiError, api, exchangeApi, importApi, projectApi, projectDataApi, scheduleApi, type ImportSummary, type Project } from '../api';
import { availableParts, partsIn } from './transfer';
import { formatDate } from '../format';

type Stage = 'input' | 'preview' | 'running' | 'done';

/**
 * Creating a project from a PST-Import file (docs/IMPORT.md).
 *
 * Three steps: paste or pick the file, look at what it will produce - with
 * everything that deserves a second look spelled out - and import. Nothing
 * is written before the last step.
 *
 * The file spans several modules. Each module imports its own part through
 * its own endpoint, so no module has to know another (ADR 0002); this dialog
 * is the one place that splits the file. Which parts are taken is chosen in
 * the preview - all the file carries, or only some.
 */
export function ImportModal({
  opened,
  onClose,
  initial,
  target,
}: {
  opened: boolean;
  onClose: () => void;
  /** A document prepared elsewhere (duplicate, template): starts at the preview. */
  initial?: ImportDocument;
  /** Import into this existing project, replacing the chosen parts. */
  target?: Project;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules, enabled: opened });
  const active = new Set((modules.data ?? []).filter((m) => m.active).map((m) => m.key));

  const [stage, setStage] = useState<Stage>('input');
  const [text, setText] = useState('');
  const [doc, setDoc] = useState<ImportDocument | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [keepBaseline, setKeepBaseline] = useState(true);
  const [parts, setParts] = useState<Set<ImportPart>>(new Set());
  const [results, setResults] = useState<string[]>([]);
  /** What the check before the import found, with the file it found it in. */
  const [issues, setIssues] = useState<{ list: ImportIssue[]; doc: ImportDocument } | null>(null);
  /** What reading an MSPDI file left out (resources, constraints …). */
  const [mspdiNotes, setMspdiNotes] = useState<string[]>([]);
  const replacing = target !== undefined;

  // A prepared document skips the paste step.
  // Waits for the module list, which decides what the preview covers.
  const modulesReady = modules.isSuccess;
  useEffect(() => {
    if (opened && initial && modulesReady) void inspect(initial);
  }, [opened, initial, modulesReady]);

  const reset = (): void => {
    setStage('input'); setText(''); setDoc(null); setSummary(null); setError(null); setCreatedId(null); setBusy(false); setParts(new Set()); setResults([]); setIssues(null);
  };
  const close = (): void => {
    reset();
    onClose();
  };
  const message = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : 'Unbekannter Fehler.');

  const inspect = async (parsed: ImportDocument): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      setSummary(parsed.schedule && active.has('scheduling') ? await importApi.previewSchedule(parsed.schedule) : null);
      const offered = availableParts(active);
      setParts(new Set(partsIn(parsed).filter((k) => offered.includes(k))));
      setDoc(parsed);
      setStage('preview');
    } catch (e) {
      setError(`Der Terminplan in der Datei ist nicht in Ordnung: ${message(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const check = async (input: string = text): Promise<void> => {
    setError(null);
    setIssues(null);
    setMspdiNotes([]);
    // A Microsoft Project file (MSPDI, AP-16): read on the server into the
    // schedule part of a PST-Import document, then the same way as any file.
    if (input.trimStart().startsWith('<')) {
      setBusy(true);
      try {
        const m = await importApi.readMspdi(input);
        const doc: ImportDocument = {
          format: IMPORT_FORMAT, version: IMPORT_VERSION,
          project: target ? { number: target.number, name: target.name } : { number: m.title.slice(0, 40), name: m.title.slice(0, 200) },
          schedule: m.schedule,
        };
        const json = JSON.stringify(doc, null, 2);
        setText(json);
        setMspdiNotes(m.notes);
        setBusy(false);
        await check(json);
        setMspdiNotes(m.notes);
      } catch (e) {
        setBusy(false);
        setError(`Die MSPDI-Datei ließ sich nicht lesen: ${message(e)}`);
      }
      return;
    }
    let parsed: ImportDocument;
    try {
      // A chat answer often wraps the file in a code fence; accept that.
      const raw = input.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
      parsed = JSON.parse(raw) as ImportDocument;
    } catch {
      setError('Das ist kein gültiges JSON. Bitte die Datei unverändert einfügen.');
      return;
    }
    if (parsed?.format !== IMPORT_FORMAT || parsed.version !== IMPORT_VERSION) {
      setError(`Unbekanntes Format. Erwartet wird "format": "${IMPORT_FORMAT}", "version": ${IMPORT_VERSION}.`);
      return;
    }
    if (!parsed.project?.number?.trim() || !parsed.project?.name?.trim()) {
      setError('Projektnummer und Projektname fehlen (project.number, project.name).');
      return;
    }
    // Every spot over a limit or out of shape, before anything is written - not
    // one at a time halfway through the import.
    try {
      const response = await fetch('/api/docs/pst-import-v1.schema.json');
      if (!response.ok) throw new Error(`Schema: ${response.status}`);
      const schema = (await response.json()) as object;
      const found = checkImportDocument(parsed, schema);
      if (found.length > 0) {
        setIssues({ list: found, doc: parsed });
        return;
      }
    } catch {
      // Without the schema the modules still check their parts on import.
    }
    setIssues(null);
    await inspect(parsed);
  };

  const run = async (): Promise<void> => {
    if (!doc) return;
    setError(null);
    setStage('running');
    let projectId = target?.id ?? createdId;
    try {
      if (!projectId) {
        const created = await projectApi.create({ number: doc.project.number.trim(), name: doc.project.name.trim() });
        projectId = created.id;
        setCreatedId(projectId);
      }
      const done: string[] = [];
      if (parts.has('projectdata')) {
        const { number: _n, name: _m, ...details } = doc.project;
        void _n; void _m;
        const r = await projectDataApi.import(projectId, { details, contacts: doc.contacts ?? [] });
        done.push(`Projektdaten übernommen, ${count(r.contacts, 'Kontakt', 'Kontakte')} neu`);
      }
      if (doc.costs && parts.has('costs')) {
        const r = await exchangeApi.replaceCosts(projectId, doc.costs);
        done.push(`Kostenplanung: ${count(r.variants, 'Variante', 'Varianten')}, ${count(r.values, 'Wert', 'Werte')}, ${count(r.items, 'Position', 'Positionen')}`);
      }
      if (doc.tendering && parts.has('tendering')) {
        const r = await exchangeApi.replaceTendering(projectId, doc.tendering);
        done.push(`Vergabe: ${count(r.packages, 'Vergabeeinheit', 'Vergabeeinheiten')}`);
      }
      if (doc.time && parts.has('time')) {
        const r = await exchangeApi.replaceTime(projectId, doc.time);
        done.push(`Zeiterfassung: ${count(r.categories, 'Kategorie', 'Kategorien')}${r.entries ? `, ${count(r.entries, 'Eintrag', 'Einträge')}` : ''}`);
      }
      if (doc.schedule && parts.has('schedule')) {
        if (replacing && keepBaseline) {
          const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const existing = await scheduleApi.tasks(projectId);
          if (existing.length > 0) await scheduleApi.createBaseline(projectId, `Stand vor Import ${today}`);
        }
        await importApi.importSchedule(projectId, doc.schedule, replacing);
        done.push('Terminplan übernommen');
      }
      setResults(done);
      // Several modules changed at once; refetching everything is simplest and cheap.
      await queryClient.invalidateQueries();
      setStage('done');
    } catch (e) {
      setError(
        replacing
          ? `Der Import ist fehlgeschlagen: ${message(e)}`
          : projectId
          ? `Das Projekt ist angelegt, aber ein Teil fehlt: ${message(e)}`
          : `Das Projekt wurde nicht angelegt: ${message(e)}`,
      );
      setStage('preview');
    }
  };

  const copyInstructions = async (): Promise<string> => {
    const [guide, schema] = await Promise.all([
      fetch('/api/docs/pst-import-anleitung.md').then((r) => r.text()),
      fetch('/api/docs/pst-import-v1.schema.json').then((r) => r.text()),
    ]);
    return `${guide}\n\n## JSON-Schema\n\n\`\`\`json\n${schema}\`\`\`\n`;
  };

  const step = stage === 'input' ? 0 : stage === 'preview' ? 1 : 2;
  const p = doc?.project;

  return (
    <Modal opened={opened} onClose={close} title={replacing ? `In ${target.number} importieren` : 'Projekt importieren'} size="xl">
      <Stack gap="md">
        <Stepper active={step} size="xs">
          <Stepper.Step label="Datei" />
          <Stepper.Step label="Prüfen" />
          <Stepper.Step label="Anlegen" />
        </Stepper>

        {error && <Alert color="red" withCloseButton onClose={() => setError(null)}>{error}</Alert>}
        {mspdiNotes.length > 0 && <Alert color="yellow" py={6} title="Aus der MSPDI-Datei nicht übernommen">{mspdiNotes.map((n) => <Text key={n} size="xs">{n}</Text>)}</Alert>}

        {stage === 'input' && (
          <Stack gap="sm">
            <Text size="sm">
              {replacing
                ? `Die Teile der Datei, die du im nächsten Schritt wählst, ersetzen die von ${target.number}: Terminplan, Kostenplanung, Vergabe. Projektdaten werden ergänzt, Kontakte nicht doppelt angelegt, Kategorien der Zeiterfassung ergänzt. Basispläne bleiben erhalten. `
                : 'Eine PST-Import-Datei legt ein neues Projekt an – mit Projektdaten, Kontakten, Terminplan, Kostenplanung, Vergabe und Zeiterfassung, je nachdem, was die Datei enthält. '}
              So entsteht sie in Claude Chat: Anleitung kopieren, zusammen mit Auslobung und Angebot einfügen,
              die ausgegebene Datei hier einfügen.
            </Text>
            <Group gap="xs">
              <InstructionsButton load={copyInstructions} />
              <Anchor size="sm" href="/api/docs/pst-import-v1.schema.json" target="_blank">Schema ansehen</Anchor>
            </Group>
            <Textarea
              label="Inhalt der Datei"
              placeholder='{ "format": "pst-import", "version": 1, … } – oder eine MSPDI-Datei (Microsoft Project XML)'
              autosize
              minRows={8}
              maxRows={16}
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              value={text}
              onChange={(e) => { setText(e.currentTarget.value); setIssues(null); }}
            />
            {issues && <IssuesPanel issues={issues.list} onShorten={() => { const next = JSON.stringify(shortenTooLong(issues.doc, issues.list), null, 2); setText(next); void check(next); }} onAnyway={() => { const d = issues.doc; setIssues(null); void inspect(d); }} />}
            <Group justify="space-between">
              <FileButton accept="application/json,.json,.xml,text/xml" onChange={(file) => file && file.text().then((t) => { setText(t); setIssues(null); })}>
                {(props) => <Button variant="default" {...props}>Datei wählen …</Button>}
              </FileButton>
              <Button onClick={() => void check()} disabled={text.trim().length === 0} loading={busy}>Prüfen</Button>
            </Group>
          </Stack>
        )}

        {(stage === 'preview' || stage === 'running') && doc && p && (
          <Stack gap="sm">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              <Fact label="Projekt" value={`${p.number} · ${p.name}`} />
              <Fact label="Auftraggebende" value={p.client?.split('\n')[0]} />
              <Fact label="Kontakte" value={count(doc.contacts?.length, 'Kontakt', 'Kontakte')} />
              <Fact label="Annahmen, Risiken" value={`${count(p.assumptions?.length, 'Annahme', 'Annahmen')} · ${count(p.risks?.length, 'Risiko', 'Risiken')}`} />
              {summary && (
                <>
                  <Fact label="Terminplan" value={`${count(summary.groups, 'Phase', 'Phasen')} · ${count(summary.tasks, 'Vorgang', 'Vorgänge')} · ${count(summary.milestones, 'Meilenstein', 'Meilensteine')}`} />
                  <Fact
                    label="Zeitraum"
                    value={`${formatDate(summary.projectStart)} – ${formatDate(summary.earliestFinish)}${summary.targetFinish ? ` (Ziel ${formatDate(summary.targetFinish)})` : ''}`}
                  />
                </>
              )}
            </SimpleGrid>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Übernehmen</Text>
              {IMPORT_PARTS.filter((part) => partsIn(doc).includes(part.key)).map((part) => {
                const on = active.has(part.module);
                return (
                  <Checkbox
                    key={part.key}
                    size="sm"
                    disabled={!on || stage === 'running'}
                    checked={on && parts.has(part.key)}
                    onChange={(e) => { const checked = e.currentTarget.checked; setParts((prev) => { const n = new Set(prev); if (checked) n.add(part.key); else n.delete(part.key); return n; }); }}
                    label={`${part.label}${partDetail(doc, part.key)}${on ? '' : ' – Modul nicht aktiv'}`}
                  />
                );
              })}
            </Stack>
            {summary && summary.notices.length > 0 && (
              <Stack gap={4}>
                <Text size="sm" fw={500}>Bitte ansehen ({summary.notices.length})</Text>
                <ScrollArea.Autosize mah={220} type="auto" scrollbars="y" offsetScrollbars>
                  <List size="sm" spacing={2}>
                    {summary.notices.map((notice, index) => <List.Item key={index}>{notice}</List.Item>)}
                  </List>
                </ScrollArea.Autosize>
              </Stack>
            )}
            {summary && summary.notices.length === 0 && <Text size="sm" c="dimmed">Keine Auffälligkeiten.</Text>}
            {replacing && [...parts].some((k) => k === 'schedule' || k === 'costs' || k === 'tendering') && (
              <Alert color="yellow" py={6}>
                In {target.number} werden vollständig ersetzt: {[...parts].filter((k) => k === 'schedule' || k === 'costs' || k === 'tendering').map((k) => IMPORT_PARTS.find((x) => x.key === k)!.label).join(', ')}. Projektnummer und -name bleiben.
              </Alert>
            )}
            {replacing && parts.has('schedule') && (
              <Stack gap={4}>
                <Checkbox
                  checked={keepBaseline}
                  onChange={(e) => setKeepBaseline(e.currentTarget.checked)}
                  label="Bisherigen Stand vorher als Basisplan festhalten (zum Vergleichen)"
                />
              </Stack>
            )}
            <Group justify="space-between">
              <Button variant="default" onClick={() => setStage('input')} disabled={stage === 'running' || createdId !== null || initial !== undefined}>Zurück</Button>
              <Button onClick={() => void run()} loading={stage === 'running'} color={replacing ? 'orange' : undefined} disabled={replacing && parts.size === 0}>
                {replacing ? 'Importieren' : createdId ? 'Fehlenden Teil erneut importieren' : 'Projekt anlegen'}
              </Button>
            </Group>
          </Stack>
        )}

        {stage === 'done' && (createdId || replacing) && (
          <Stack gap="sm">
            <Alert color="green" title={replacing ? 'Importiert' : 'Projekt angelegt'}>
              {replacing ? `${target.number}: ` : `${p?.number} · ${p?.name} ist importiert. `}
              {results.join(' · ')}.
              {summary && parts.has('schedule') && ` ${count(summary.tasks, 'Vorgang', 'Vorgänge')} und ${count(summary.milestones, 'Meilenstein', 'Meilensteine')} liegen auf den Terminen der Datei.`}
            </Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>Schließen</Button>
              <Button
                onClick={() => {
                  close();
                  navigate(`/projekte/${target?.id ?? createdId}/${parts.has('schedule') ? 'termine' : parts.has('costs') ? 'kosten' : parts.has('tendering') ? 'ausschreibung' : parts.has('time') ? 'zeiten' : 'projekt'}`);
                }}
              >
                Zum Projekt
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}

/** A short count of what a part carries, for the checkbox label. */
function partDetail(doc: ImportDocument, part: ImportPart): string {
  if (part === 'costs' && doc.costs) {
    const c = doc.costs;
    return ` (${[c.variants?.length && count(c.variants.length, 'Variante', 'Varianten'), count(c.groups?.length, 'Kostengruppe', 'Kostengruppen'), c.values?.length && count(c.values.length, 'Wert', 'Werte'), count(c.items?.length, 'Position', 'Positionen')].filter(Boolean).join(', ')})`;
  }
  if (part === 'tendering' && doc.tendering) return ` (${count(doc.tendering.packages.length, 'Vergabeeinheit', 'Vergabeeinheiten')})`;
  if (part === 'time' && doc.time) return ` (${count(doc.time.categories.length, 'Kategorie', 'Kategorien')}${doc.time.entries?.length ? `, ${count(doc.time.entries.length, 'Eintrag', 'Einträge')} – werden auf dich gebucht` : ''})`;
  if (part === 'projectdata') return ` (${count(doc.contacts?.length, 'Kontakt', 'Kontakte')})`;
  return '';
}

function count(n: number | undefined, one: string, many: string): string {
  const value = n ?? 0;
  return `${value} ${value === 1 ? one : many}`;
}

function Fact({ label, value }: { label: string; value: string | undefined }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{value || '—'}</Text>
    </Stack>
  );
}

/** Loads the instructions once, then offers them for copying. */
function InstructionsButton({ load }: { load: () => Promise<string> }): JSX.Element {
  const [content, setContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  if (content === null) {
    return (
      <Button
        variant="light"
        onClick={() => load().then(setContent).catch(() => setFailed(true))}
        color={failed ? 'red' : undefined}
      >
        {failed ? 'Anleitung nicht ladbar' : 'Anleitung für Claude laden'}
      </Button>
    );
  }
  return (
    <CopyButton value={content}>
      {({ copied, copy }) => (
        <Button variant="light" color={copied ? 'green' : undefined} onClick={copy}>
          {copied ? 'Kopiert – in Claude Chat einfügen' : 'Anleitung für Claude kopieren'}
        </Button>
      )}
    </CopyButton>
  );
}

/**
 * What the check before the import found. Texts over their limit can be cut
 * here; everything else goes back to Claude Chat as a list to mend.
 */
function IssuesPanel({ issues, onShorten, onAnyway }: { issues: ImportIssue[]; onShorten: () => void; onAnyway: () => void }): JSX.Element {
  const long = issues.filter((i) => i.tooLong);
  const other = issues.length - long.length;
  return (
    <Alert color="orange" title={`Die Datei hat ${issues.length === 1 ? 'eine Stelle' : `${issues.length} Stellen`}, an denen der Import scheitern würde`}>
      <Stack gap="xs">
        <ScrollArea.Autosize mah={240} type="auto" scrollbars="y" offsetScrollbars>
          <List size="xs" spacing={2}>
            {issues.slice(0, 200).map((i, n) => <List.Item key={n}><Text span size="xs" ff="monospace">{i.where}</Text>: {i.message}</List.Item>)}
          </List>
          {issues.length > 200 && <Text size="xs" c="dimmed">… und {issues.length - 200} weitere (in der kopierten Liste vollständig)</Text>}
        </ScrollArea.Autosize>
        <Text size="xs">Am besten die Liste kopieren und in Claude Chat mit der Bitte einfügen, die Datei zu korrigieren.{long.length > 0 ? ' Zu lange Texte kann PST auch selbst kürzen (das Ende wird durch „…“ ersetzt).' : ''}</Text>
        <Group gap="xs">
          <CopyButton value={issuesAsText(issues)}>
            {({ copied, copy }) => <Button size="xs" variant="light" onClick={copy}>{copied ? 'Kopiert' : 'Liste für Claude kopieren'}</Button>}
          </CopyButton>
          {long.length > 0 && other === 0 && <Button size="xs" onClick={onShorten}>{long.length === 1 ? 'Den zu langen Text kürzen' : `${long.length} zu lange Texte kürzen`} und weiter</Button>}
          {long.length > 0 && other > 0 && <Button size="xs" variant="default" onClick={onShorten}>{long.length === 1 ? 'Den zu langen Text' : `${long.length} zu lange Texte`} kürzen, dann neu prüfen</Button>}
          <Button size="xs" variant="subtle" color="gray" onClick={onAnyway}>trotzdem weiter</Button>
        </Group>
      </Stack>
    </Alert>
  );
}
